// src/features/tienda/hooks.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyStore,
  listMyKoHubCases,
  listMyListings,
  listMyPayouts,
  listMySales,
  updateMyStore,
} from '@/api/endpoints/tienda';
import { useAuthStore } from '@/auth/auth-store';
import type { MyStore } from '@/api/schemas/tienda';

export const tiendaKeys = {
  all: () => ['tienda'] as const,
  store: () => ['tienda', 'store'] as const,
  listings: (storeId?: string, status?: string) =>
    ['tienda', 'listings', storeId ?? 'all', status ?? 'all'] as const,
  sales: (storeId?: string, status?: string) =>
    ['tienda', 'sales', storeId ?? 'all', status ?? 'all'] as const,
  kohub: (storeId?: string, status?: string) =>
    ['tienda', 'kohub', storeId ?? 'all', status ?? 'all'] as const,
  payouts: () => ['tienda', 'payouts'] as const,
};

/**
 * The signed-in owner's stores, ACTIVE first and the primary one first
 * (ko-backend#1279). A backend older than that answers one `profile`, which
 * becomes a list of one.
 */
export function useMyStores(enabled = true) {
  const q = useQuery({ queryKey: tiendaKeys.store(), queryFn: getMyStore, enabled });
  const stores: MyStore[] = q.data?.stores ?? (q.data?.profile ? [q.data.profile] : []);
  return { ...q, stores };
}

/** Whether the signed-in principal is a store owner (role USER in the panel). */
export function useIsStoreOwner(): boolean {
  return useAuthStore((s) => s.user?.role === 'USER');
}

/**
 * Each list is about the active store (`storeId`); the backend answers 404
 * for a store that is not the owner's, so a stale choice fails loudly rather
 * than showing somebody else's rows. Without a store yet, nothing is asked.
 */
export function useMyListings(storeId: string | undefined, status?: string) {
  return useQuery({
    queryKey: tiendaKeys.listings(storeId, status),
    queryFn: () => listMyListings({ status, storeId }),
    enabled: storeId !== undefined,
  });
}

export function useMySales(storeId: string | undefined, status?: string) {
  return useQuery({
    queryKey: tiendaKeys.sales(storeId, status),
    queryFn: () => listMySales({ status, storeId }),
    enabled: storeId !== undefined,
  });
}

export function useMyKoHubCases(storeId: string | undefined, status?: string) {
  return useQuery({
    queryKey: tiendaKeys.kohub(storeId, status),
    queryFn: () => listMyKoHubCases({ status, storeId }),
    enabled: storeId !== undefined,
  });
}

export function useMyPayouts() {
  return useQuery({ queryKey: tiendaKeys.payouts(), queryFn: listMyPayouts });
}

export function useUpdateMyStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ storeId, ...body }: { storeId: string; pickupInstructions: string | null }) =>
      updateMyStore(storeId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tiendaKeys.store() }),
  });
}
